/**
 * AIInputModal - Natural language input for cashflow entries
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Sparkles, Loader2, AlertCircle } from 'lucide-react';
import { useCashflowStore } from '../../stores/cashflowStore';

const EXAMPLE_COMMANDS = [
  'Her 2 haftada bir çarşamba 3000 USD maaş Enpara hesabıma',
  'Her ayın 24ünde Netflix 150 TL kredi kartından',
  'Her ayın 15inde kira yatacak 108k TL',
  'Bu cuma 5000 TL market alışverişi',
  'Her ayın 1inde elektrik faturası 500 TL',
];

const AIInputModal = () => {
  const {
    closeAIModal,
    parseCommand,
    isParsingCommand,
    parseError,
    aiCommand,
    setAICommand,
  } = useCashflowStore();

  const [localCommand, setLocalCommand] = useState(aiCommand);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!localCommand.trim() || isParsingCommand) return;
    await parseCommand(localCommand.trim());
  };

  const handleExampleClick = (example) => {
    setLocalCommand(example);
    setAICommand(example);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-lg bg-gray-900 border border-gray-800 rounded-xl shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">AI ile Yeni Giriş</h2>
              <p className="text-sm text-gray-400">Doğal dilde finansal işlem ekleyin</p>
            </div>
          </div>
          <button
            onClick={closeAIModal}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Input area */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Finansal işleminizi yazın
            </label>
            <textarea
              value={localCommand}
              onChange={(e) => {
                setLocalCommand(e.target.value);
                setAICommand(e.target.value);
              }}
              placeholder="Örn: Her ayın 15inde maaş yatacak 45000 TL Akbank hesabına"
              className="w-full h-24 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 resize-none"
              disabled={isParsingCommand}
              autoFocus
            />
          </div>

          {/* Error message */}
          {parseError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-2 p-3 bg-red-900/30 border border-red-700/50 rounded-lg"
            >
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{parseError}</p>
            </motion.div>
          )}

          {/* Examples */}
          <div>
            <p className="text-xs text-gray-500 mb-2">Örnekler:</p>
            <div className="flex flex-wrap gap-2">
              {EXAMPLE_COMMANDS.slice(0, 3).map((example, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleExampleClick(example)}
                  className="text-xs px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-full transition-colors"
                  disabled={isParsingCommand}
                >
                  {example.length > 40 ? example.substring(0, 40) + '...' : example}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={closeAIModal}
              className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
              disabled={isParsingCommand}
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={!localCommand.trim() || isParsingCommand}
              className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isParsingCommand ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analiz ediliyor...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  AI ile Ekle
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default AIInputModal;
